import React, { useCallback, useRef, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { notify } from '../../../utils/notification';
import useCampaignSpellSync from '../hooks/useCampaignSpellSync';
import { decideCastWindow, syncCastWindows } from '../utils/castWindowService';
import { reactionSlotOptions } from '../utils/reactionSlotOptions';

/** One line for what happened: the save, then the outcome. */
export const describeCounterspell = ({ result, casterName, spellName }) => `Counterspell: ${casterName} rolled ${result.total} against DC ${result.dc} and ${result.success ? `saved, so ${spellName} goes ahead` : `failed, so ${spellName} is countered`}.`;

/** The moment an enemy casts: Counterspell as it starts, or Shield once Magic Missile picks this character. The DM waits. */
export default function CastWindowPrompt({ campaignId, characterId, form, onCombatChange }) {
  const [windows, setWindows] = useState([]), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [slot, setSlot] = useState('0'), [canSee, setCanSee] = useState(false);
  const lock = useRef(false);
  const load = useCallback(async () => (await syncCastWindows(campaignId, { characterId: String(characterId) })).windows || [], [campaignId, characterId]);
  useCampaignSpellSync({ campaignId: characterId ? campaignId : null, kind: 'fateWindows', load,
    onData: next => setWindows(previous => (JSON.stringify(previous) === JSON.stringify(next) ? previous : next)) });
  const offer = windows[0];
  const counter = offer?.kind === 'counterspell';
  const slots = offer ? reactionSlotOptions(form).filter(option => option.slotLevel >= offer.minSlotLevel) : [];
  const chosen = slots[Number(slot)] || slots[0];
  const decide = async decision => {
    if (!offer || lock.current) return;
    lock.current = true; setBusy(true); setError('');
    try {
      const result = await decideCastWindow(campaignId, offer.windowId, { helperId: String(characterId), decision,
        ...(decision === 'use' ? { pool: chosen?.pool, slotLevel: chosen?.slotLevel, ...(counter ? { canSee } : {}) } : {}) });
      if (result.combat) onCombatChange?.(result.combat);
      if (decision === 'use') notify(counter ? describeCounterspell({ result: result.result, casterName: offer.casterName, spellName: offer.spellName }) : 'Shield: Magic Missile does nothing to you.', 'success');
      setWindows(previous => previous.filter(item => item.windowId !== offer.windowId));
      setSlot('0'); setCanSee(false);
    } catch (failure) { setError(failure.message); }
    finally { lock.current = false; setBusy(false); }
  };
  if (!offer) return null;
  const title = counter ? 'Counterspell' : 'Shield';
  return <Modal show centered backdrop="static" keyboard={false} className="dnd-modal modern-modal" aria-label={title}>
    <Modal.Header><Modal.Title>{title}</Modal.Title></Modal.Header>
    <Modal.Body>
      {error && <Alert variant="danger">{error}</Alert>}
      <p>{counter ? `${offer.casterName} is casting ${offer.spellName}. Use your Reaction to make it attempt a Constitution save? On a failure the spell has no effect.`
        : `${offer.casterName} aimed Magic Missile at you. Use your Reaction to take no damage from it, with +5 AC until your next turn?`}</p>
      {slots.length > 1 && <Form.Select className="mb-2" aria-label="Spell slot" value={slot} disabled={busy} onChange={event => setSlot(event.target.value)}>
        {slots.map((option, index) => <option key={`${option.pool}:${option.slotLevel}`} value={index}>{option.label}</option>)}
      </Form.Select>}
      {counter && <Form.Check id="counterspell-can-see" type="checkbox" label={`I can see ${offer.casterName}`} checked={canSee} disabled={busy} onChange={event => setCanSee(event.target.checked)} />}
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" disabled={busy} onClick={() => decide('skip')}>Skip</Button>
      <Button disabled={busy || !chosen || (counter && !canSee)} onClick={() => decide('use')}>{busy ? 'Casting…' : `Cast ${title}`}</Button>
    </Modal.Footer>
  </Modal>;
}
